import moment from 'moment-timezone'
import { hasPermissions } from '@common/utils/validation/authValidators'
import Device, {
  DeviceAPI,
  DeviceBase,
  DeviceBaseAPI,
  DeviceIDTypes,
  DeviceSettings,
  DeviceSettingsAPI,
  StagedDeviceAPI,
  TestDeviceAPI,
  Valve,
  valveExtendedKey,
  valveExtendedKeyInDB,
  ValveKey,
  ValveRecoveryPolicy,
  ValveSettingsAPI,
  ValveStateExtendedAPI,
  ValveStateExtendedSlots,
} from '@data/device/model/device'
import { TokenPermission } from '@data/rbac/model/rbac'

// Firmware still reports a sixth valve that can never be physically installed. The backend
// has dropped V6 from its schemas, so this guards the deploy overlap only.
export const VALVE_KEYS: ValveKey[] = ['V1', 'V2', 'V3', 'V4', 'V5']

// Keyed by ICCID prefix so the filter option list and the rendered value come from one
// source. A table filter offering an MVNO no device can display, or displaying one the
// filter cannot select, is the drift this shape prevents. A Map rather than an object
// literal for two reasons: lookups return `V | undefined` honestly, so the fallbacks below
// need no cast asserting a key that may not be there; and Map keeps insertion order, where
// an object with these integer-like keys reorders them numerically.
const MVNO_BY_ICCID_PREFIX = new Map([
  ['894453', 'EsEye'],
  ['894573', 'Onomondo'],
  ['898830', 'Emnify'],
  ['890188', 'Simpl'],
])

// Sorted for a predictable filter dropdown, not to correct an ordering hazard — the Map
// removed that.
export const MVNO_VALUES = [...MVNO_BY_ICCID_PREFIX.values()].sort()

// Prefix match, so this cannot be a plain `get`.
export const mapMvno = (iccid?: string) => {
  if (!iccid) return ''

  return [...MVNO_BY_ICCID_PREFIX].find(([prefix]) => iccid.startsWith(prefix))?.[1]
}

// Same one-source rule as MVNO above. An unrecognised vendor falls through to its raw name
// rather than blanking, which is the pre-existing behaviour — those devices exist.
const VENDOR_NAMES = new Map([
  ['SMARTFLOW', 'SF.H'],
  ['METASPHERE', 'SF.M'],
  ['PLUM', 'SF.P'],
  ['BADGER', 'SF.B'],
  ['ADEUNIS', 'SF.A'],
])

export const VENDOR_NAME_VALUES = [...VENDOR_NAMES.values()].sort()

export const mapVendorName = (vendor: string) => VENDOR_NAMES.get(vendor) ?? vendor ?? ''

export const mapDeviceSettings = (deviceSettings: DeviceSettingsAPI): DeviceSettings => {
  return {
    id: deviceSettings.id,
    deviceState: deviceSettings.deviceState,
    dataAcqMode: deviceSettings.data_acq_mode?.toString(),
    serverPulseControl: deviceSettings.server_pulse_control,
    pulsesPerLiter: deviceSettings.pulses_per_liter,
    firmwareVersionDesired: deviceSettings.firmware_version_desired,
    timezone: deviceSettings.timezone,
    installDate: deviceSettings.install_date,
    usageStartDate: deviceSettings.usage_start_date,
    monitoringStartDate: deviceSettings.monitoring_start_date,
    deviceIDActivationDate: deviceSettings.device_id_activation_date,
    installDateEpoch: moment(deviceSettings.install_date).unix(),
    currency: deviceSettings.currency,
    cost: deviceSettings.cost,
    report30Day: deviceSettings.report_30_day,
    report30DayCallDate: deviceSettings.call_date_30_day,
    sustainabilityReport: deviceSettings.sustainability_report,
    esgReport: deviceSettings.esg_report,
    setUpPayment: deviceSettings.set_up_payment,
    renewalOverdueStatus: deviceSettings.renewal_overdue_status,
    sectorType: deviceSettings.sector_type,
    type: deviceSettings.hot ? 'Hot' : 'Cold',
    masterDeviceIdRef: deviceSettings.master_device_id_ref,
    occupants: deviceSettings.occupants,
    hot: deviceSettings.hot || false,
    deviceName: deviceSettings.device_name,
    deviceNameShort: deviceSettings.device_name_short,
    uploadFreqMins: deviceSettings.upload_freq_mins,
    location: deviceSettings.location ?? '',
    uom: deviceSettings.uom,
    conversionFactor: deviceSettings.conversion_factor,
    monitoringEnabled: deviceSettings.monitoring_enabled,
    aasEnabled: deviceSettings.aas_enabled,
    co2Rate: deviceSettings.co2_rate,
    clientMeterId: deviceSettings.client_meter_id || '',
    usageLoggingEnabled: deviceSettings.usage_logging_enabled,
    vacant: deviceSettings.vacant,
    noFlowThresholdHours: deviceSettings.no_flow_threshold_hours,
    missedCheckinThreshold: deviceSettings.missed_checkin_threshold,
    hasValveSchedule: deviceSettings.has_valve_schedule,
  }
}

// The API base, mapped once for every device type so `mapDevice` and
// `mapUnprovisionedDevice` cannot drift on it. `vendorId` comes out optional to suit test and
// staged devices; `mapDevice` narrows it, since `DevicePublic` requires it.
const mapDeviceBase = (device: DeviceBaseAPI) => ({
  deviceId: String(device.device_id),
  state: device.state,
  model: device.model,
  deviceVendor: device.vendor,
  deviceVendorName: mapVendorName(device.vendor),
  vendorId: device.vendor_id ?? undefined,
  valveOperation: device.valve_operation,
  rtfOperation: device.rtf_operation,
  hlfOperation: device.hlf_operation,
  resetOperation: device.reset_operation,
  fwOperation: device.fw_operation,
  statusOperation: device.status_operation,
  azureDeleted: device.azure_deleted ?? undefined,
  azureDeletedDate: device.azure_deleted_date
    ? new Date(device.azure_deleted_date)
    : undefined,
})

export const mapDevice = (deviceData: DeviceAPI, permissions: TokenPermission[]): Device => {
  // `device_status` is optional and nullable on `DevicePublic`; bound to a const so the
  // reads below stay guarded rather than repeating the optional chain from `deviceData`.
  const status = deviceData.device_status

  return {
    ...mapDeviceBase(deviceData),
    vendorId: deviceData.vendor_id,
    childDeviceIds: deviceData.child_device_ids ?? [],
    dlId: deviceData.dl_id,
    dUuid: deviceData.d_uuid,
    accountId: String(deviceData.account_id),
    deviceName: deviceData.device_settings.device_name,
    deviceSettings: { ...mapDeviceSettings(deviceData.device_settings) },
    batteryLevel: status?.power?.HUB_BAT ?? null,
    batteryLevelExt: status?.power?.ext_battery ?? null,
    batteryStatus: status?.power?.HUB_STA ?? '',
    signal: status?.power?.CSQ ?? null,
    ICCID: status?.network_info?.ICCID ?? '',
    connectionStatus:
      mapConnectionState(
        permissions,
        deviceData.device_settings.upload_freq_mins,
        status?.last_ping,
      ) ?? 'Error',
    firmware: status?.device_sw?.HUB_SW ?? '',
    binFileName: status?.bin_file_name ?? '',
    binFilePath: status?.bin_file_path ?? '',
    deviceLocationId: String(deviceData.customer_location_id),
    onBattDate: status?.on_batt_date
      ? new Date(status.on_batt_date)
      : undefined,
    mvno: mapMvno(status?.network_info?.ICCID),
    lastOnline: status?.last_ping
      ? new Date(status.last_ping)
      : undefined,
    valves: { ...mapValves(deviceData) },
    valveRecovery: (status?.valveRecovery ?? null) as ValveRecoveryPolicy | null,
    networkName: status?.network_info?.network_name || 'Unknown',
    fplmnList: status?.network_info?.fplmn_list,
    comments: deviceData.comments ?? undefined,
    stripeSubscriptionId: deviceData.stripe_subscription_id ?? null,
  }
}

// Test and staged devices share one schema shape, so one mapper serves both. Neither carries
// `device_settings` any more, so there is no device name to fall back to — the ID is the
// only label. Returns `DeviceBase`, which `TestDevice` and `StagedDevice` are both
// structurally identical to, so each query types its own result with no cast.
export const mapUnprovisionedDevice = (device: TestDeviceAPI | StagedDeviceAPI): DeviceBase => ({
  ...mapDeviceBase(device),
  deviceName: device.device_id,
  // `firmware_version` is an API-side alias for `device_status.device_sw.HUB_SW`, so the two
  // cannot disagree and the order below carries no meaning — do not spend time on it. The
  // fallback exists only because the schema makes each independently nullable: `HUB_SW` is
  // unreachable when `device_status` is absent, which `DeviceStatusInDB` allows.
  firmware: device.device_status?.device_sw?.HUB_SW || device.firmware_version || '',
  binFileName: device.device_status?.bin_file_name ?? '',
  binFilePath: device.device_status?.bin_file_path ?? '',
  batteryLevel: device.device_status?.power?.HUB_BAT ?? null,
  batteryLevelExt: device.device_status?.power?.ext_battery ?? null,
  batteryStatus: device.device_status?.power?.HUB_STA ?? '',
  signal: device.device_status?.power?.CSQ ?? null,
  ICCID: device.device_status?.network_info?.ICCID ?? '',
  mvno: mapMvno(device.device_status?.network_info?.ICCID),
  networkName: device.device_status?.network_info?.network_name || 'Unknown',
  // Empty rather than 'Error' when absent: this is the raw `connection_state`, shown as-is in
  // the Test Bench modal, and a device that has reported no state has not reported an error.
  connectionStatus: device.device_status?.connection_state ?? '',
  lastOnline: device.device_status?.last_ping
    ? new Date(device.device_status.last_ping)
    : undefined,
  onBattDate: device.device_status?.on_batt_date
    ? new Date(device.device_status.on_batt_date)
    : undefined,
  valves: mapValveSlots((key) =>
    device.device_status?.valve_state_extended?.[valveExtendedKeyInDB(key)],
  ),
  valveRecovery: (device.device_status?.valve_recovery ?? null) as ValveRecoveryPolicy | null,
})

// Settings are fetched separately from GET /valve_settings. The response is sparse and
// authoritative — a valve missing from it has no settings and is cleared rather than left
// alone. Valve slots always come from valveStateExtended.
export const applyValveSettings = (
  valves: { [key in ValveKey]: Valve },
  settings: ValveSettingsAPI[] | null,
): { [key in ValveKey]: Valve } => {
  const byValveId = new Map(
    (settings ?? []).map((setting) => [setting.valveId, setting]),
  )

  return Object.values(valves).reduce(
    (acc, valve) => {
      const setting = byValveId.get(valve.key)
      acc[valve.key] = {
        ...valve,
        name: setting?.name ?? undefined,
        manufacturer: setting?.manufacturer ?? undefined,
        size: setting?.size ?? undefined,
      }
      return acc
    },
    {} as { [key in ValveKey]: Valve },
  )
}

const valveFromSlot = (key: ValveKey, slot: ValveStateExtendedAPI): Valve => ({
  key,
  state: slot.s,
  position: slot.p,
  command: slot.c,
  travelMs: slot.t,
})

// Merges fresh state onto valves that already exist, so settings (name, manufacturer,
// size) survive. Used for valve probe results, which carry no settings.
export const applyValveStateExtended = (
  valves: { [key in ValveKey]: Valve },
  extended: ValveStateExtendedSlots,
): { [key in ValveKey]: Valve } => {
  return Object.values(valves).reduce(
    (acc, valve) => {
      const slot = extended[valveExtendedKey(valve.key)]
      acc[valve.key] = slot ? { ...valve, ...valveFromSlot(valve.key, slot) } : valve
      return acc
    },
    {} as { [key in ValveKey]: Valve },
  )
}

// Valve slots come from the extended valve state, not `valveState` — a null slot means the
// device reported a short array, or has not reported since extended data was introduced (all
// slots null; backfilled 2026-09-25). Either way that valve is omitted rather than rendered with
// placeholder state. Takes a
// slot reader because provisioned and unprovisioned devices key the same slots differently
// (`v1Extended` against `v1_extended`).
const mapValveSlots = (
  slotFor: (key: ValveKey) => ValveStateExtendedAPI | null | undefined,
) => VALVE_KEYS.reduce(
  (acc, key) => {
    const slot = slotFor(key)
    if (slot) acc[key] = valveFromSlot(key, slot)
    return acc
  },
  {} as { [key in ValveKey]: Valve },
)

const mapValves = (deviceData: DeviceAPI) =>
  mapValveSlots((key) => deviceData.device_status?.valveStateExtended?.[valveExtendedKey(key)])

const mapConnectionState = (
  permissions: TokenPermission[],
  uploadFreqMins?: number,
  lastPing?: string,
): string => {
  const intervalMultiplierUser: number = 12
  const intervalMultiplierEng: number = 3
  if (!lastPing) return 'Disconnected'
  if (!uploadFreqMins) return 'Error'
  const lastOnline = new Date(lastPing)
  const interval = hasPermissions(permissions, ['DASHBOARD:STATUS:VIEW'])
    ? uploadFreqMins * intervalMultiplierEng
    : uploadFreqMins * intervalMultiplierUser
  if (moment.utc().diff(lastOnline, 'minutes') >= interval) {
    return 'Disconnected'
  } else {
    return 'Connected'
  }
}

export const enumDeviceIdTypeByValue = (value: string) => {
  const indexEnum = Object.values(DeviceIDTypes).indexOf(value as unknown as DeviceIDTypes)
  return Object.keys(DeviceIDTypes)[indexEnum] as keyof Device
}
